import {
  confirmSignIn as amplifyConfirmSignIn,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  fetchUserAttributes,
  getCurrentUser,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DEV_ADMIN_EMAIL, DEV_ADMIN_SUB, DEV_AUTH } from "./cognito-config";
import {
  AuthContext,
  type AuthContextValue,
  type AuthStatus,
  type AuthUser,
  type SignInStep,
} from "./context";

/**
 * Sign-in against the ADMIN pool.
 *
 * Three things about that pool shape this file, and all three are settings
 * rather than decisions made here:
 *
 *   - Accounts are admin-created, so a first sign-in lands on
 *     `CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED` and the screen has to
 *     ask for a real password before anything else happens.
 *   - A second factor may be demanded or may need setting up, hence the two
 *     TOTP steps.
 *   - There is no self sign-up, no confirm-code, no resend. This provider
 *     deliberately exposes no way to create an account: the only path in is
 *     `aws cognito-idp admin-create-user`, which is the point.
 *
 * The dev bypass short-circuits all of it. When `VITE_ADMIN_DEV_AUTH=1` in a
 * dev build there is no pool, no token and no Amplify call — the session is
 * simply signed in, and `ApiProvider` sends the impersonation headers that
 * `pnpm dev` in the admin backend trusts. It is gated on
 * `import.meta.env.DEV` as well as the variable, so a production bundle
 * cannot contain this path at all.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(
    DEV_AUTH ? "signed_in" : "loading",
  );
  const [user, setUser] = useState<AuthUser | null>(
    DEV_AUTH ? { username: DEV_ADMIN_SUB, email: DEV_ADMIN_EMAIL } : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<SignInStep>("idle");

  const checkAuthState = useCallback(async (): Promise<void> => {
    try {
      const current = await getCurrentUser();
      let email = current.signInDetails?.loginId ?? current.username;
      try {
        const attributes = await fetchUserAttributes();
        if (attributes.email) email = attributes.email;
      } catch {
        // Attributes are occasionally unavailable mid-session. The login id
        // is a good enough name for the topbar.
      }
      setUser({ username: current.username, email });
      setStatus("signed_in");
      setStep("idle");
    } catch {
      setUser(null);
      setStatus("signed_out");
    }
  }, []);

  useEffect(() => {
    if (DEV_AUTH) return;
    void checkAuthState();
  }, [checkAuthState]);

  useEffect(() => {
    if (DEV_AUTH) return;
    return Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
          void checkAuthState();
          break;
        case "signedOut":
        case "tokenRefresh_failure":
          setUser(null);
          setStatus("signed_out");
          setStep("idle");
          break;
      }
    });
  }, [checkAuthState]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      error,
      step,
      isDevSession: DEV_AUTH,
      async signIn(email, password) {
        setError(null);
        try {
          const result = await amplifySignIn({ username: email, password });
          if (result.isSignedIn) {
            await checkAuthState();
            return;
          }
          const next = result.nextStep.signInStep;
          if (next === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
            setStep("totp_required");
          } else if (next === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP") {
            setStep("totp_setup_required");
          } else if (next === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
            setStep("password_reset_required");
          } else {
            setError(`This account needs a step we do not handle: ${next}`);
          }
        } catch (caught) {
          setError(message(caught, "Could not sign in."));
          throw caught;
        }
      },
      async confirmTotp(code) {
        setError(null);
        try {
          const result = await amplifyConfirmSignIn({
            challengeResponse: code,
          });
          if (result.isSignedIn) await checkAuthState();
          else setError(`Unexpected next step: ${result.nextStep.signInStep}`);
        } catch (caught) {
          setError(message(caught, "That code was not accepted."));
          throw caught;
        }
      },
      async completeNewPassword(newPassword) {
        setError(null);
        try {
          const result = await amplifyConfirmSignIn({
            challengeResponse: newPassword,
          });
          if (result.isSignedIn) {
            await checkAuthState();
          } else if (
            result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE"
          ) {
            // A new password can be followed straight by the second factor.
            setStep("totp_required");
          }
        } catch (caught) {
          setError(message(caught, "Could not set that password."));
          throw caught;
        }
      },
      async signOut() {
        setError(null);
        try {
          if (!DEV_AUTH) await amplifySignOut();
        } finally {
          setUser(null);
          setStatus("signed_out");
          setStep("idle");
        }
      },
    }),
    [status, user, error, step, checkAuthState],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function message(caught: unknown, fallback: string): string {
  return caught instanceof Error && caught.message ? caught.message : fallback;
}
